import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  FiHome,
  FiGrid,
  FiUsers,
  FiBarChart2,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiTag,
  FiDollarSign,
  FiFileText,
  FiFolder,
  FiHash,
  FiEdit3,
  FiChevronDown,
  FiChevronUp,
  FiShield,
  FiPieChart,
  FiArchive
} from 'react-icons/fi';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

const AdminSidebar = ({ activeTab, setActiveTab, sidebarOpen, setSidebarOpen, isMobile, userRole = 'admin' }) => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const [isMinimized, setIsMinimized] = useState(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('adminSidebarMinimized');
      return saved === 'true';
    }
    return false;
  });

  const [expandedMenus, setExpandedMenus] = useState({ blog_parent: false });

  // Save to localStorage when changed
  useEffect(() => {
    localStorage.setItem('adminSidebarMinimized', isMinimized);
  }, [isMinimized]);

  const toggleExpanded = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const allMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: FiHome, roles: ['admin'] },
    { id: 'apps', label: 'Aplicativos', icon: FiGrid, roles: ['admin'] },
    { id: 'archived_apps', label: 'Arquivados', icon: FiArchive, roles: ['admin'] },
    { id: 'users', label: 'Usuários', icon: FiUsers, roles: ['admin'] },
    { id: 'promotions', label: 'Promoções', icon: FiTag, roles: ['admin'] },
    { id: 'pricing', label: 'Valores', icon: FiDollarSign, roles: ['admin'] },
    { id: 'analytics', label: 'Analytics', icon: FiBarChart2, roles: ['admin'] },
    { 
      id: 'blog_parent', 
      label: 'Blog', 
      icon: FiFileText,
      roles: ['admin', 'editor'],
      subItems: [
        { id: 'blog_analytics', label: 'Relatórios', icon: FiPieChart, roles: ['admin', 'editor'] },
        { id: 'posts', label: 'Posts', icon: FiFileText, roles: ['admin', 'editor'] },
        { id: 'categories', label: 'Categorias', icon: FiFolder, roles: ['admin', 'editor'] },
        { id: 'tags', label: 'Tags', icon: FiHash, roles: ['admin', 'editor'] },
        { id: 'authors', label: 'Autores', icon: FiEdit3, roles: ['admin', 'editor'] },
        { id: 'blog_editors', label: 'Equipe (Editores)', icon: FiShield, roles: ['admin'] },
      ]
    },
  ];

  // Filter based on userRole
  const menuItems = allMenuItems
    .filter(item => item.roles.includes(userRole))
    .map(item => {
      if (item.subItems) {
        return {
          ...item,
          subItems: item.subItems.filter(sub => sub.roles.includes(userRole))
        };
      }
      return item;
    });

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // No mobile fechado, não mostrar o sidebar
  if (isMobile && !sidebarOpen) {
    return null;
  }

  return (
    <>
      {/* Overlay apenas no MOBILE quando sidebar está aberta */}
      {isMobile && (
        <div
          className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-all duration-300 ease-out ${sidebarOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
            }`}
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar - Desktop: fixo (sticky), Mobile: overlay (fixed) */}
      <div
        className={`
          ${isMobile ? 'fixed' : 'sticky'}
          ${isMobile ? 'top-0 h-full' : 'top-0 h-screen'}
          left-0
          ${isMobile ? 'z-50' : 'z-10'}
          ${isMinimized ? 'w-16' : 'w-64'}
          ${isMobile ? 'max-w-full' : ''}
          ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}
          border-r shadow-sm
          transform ${isMobile ? 'transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]' : 'transition-all duration-300 ease-out'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col flex-shrink-0 overflow-hidden
        `}
      >
        {/* Conteúdo do Sidebar */}
        <div className="flex flex-col h-full flex-1">
          {/* LOGO E TOGGLE AREA */}
          <div className={`h-16 flex items-center ${isMinimized ? 'justify-center' : 'justify-between px-4'} border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-50/50'}`}>
            {!isMinimized && (
              <h1 className={`text-xl font-bold tracking-wide ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>eCobrança</h1>
            )}
            {!isMobile && (
              <button
                onClick={toggleMinimize}
                className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-900'}`}
                title={isMinimized ? "Expandir menu" : "Minimizar menu"}
              >
                {isMinimized ? <FiChevronRight className="w-5 h-5" /> : <FiChevronLeft className="w-5 h-5" />}
              </button>
            )}
          </div>

          <nav className={`flex-1 ${isMinimized ? 'px-2 py-4 overflow-hidden' : 'p-4 overflow-y-auto custom-scrollbar'}`}>
            {/*
            {!isMinimized && (
              <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 truncate transition-opacity duration-300">
                Administração
              </p>
            )}
            */}
            <ul className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isParent = !!item.subItems;

                if (isParent) {
                  const isExpanded = expandedMenus[item.id];
                  const hasActiveChild = item.subItems.some(sub => sub.id === activeTab);
                  const isParentActive = hasActiveChild;

                  return (
                    <li key={item.id} className="space-y-1">
                      <button
                        onClick={() => {
                          if (isMinimized) toggleMinimize();
                          toggleExpanded(item.id);
                        }}
                        className={`
                          flex items-center w-full justify-between
                          group relative transition-all duration-200 ease-out
                          ${isMinimized ? 'w-11 h-11 mx-auto rounded-md' : 'h-11 px-3 rounded-md'}
                          ${isParentActive
                            ? (isDarkMode ? 'bg-gray-800 text-white font-medium' : 'bg-gray-100 text-gray-900 font-medium')
                            : (isDarkMode ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900')
                          }
                        `}
                        title={isMinimized ? item.label : ''}
                      >
                        <div className={`flex items-center w-full ${isMinimized ? 'justify-center' : 'justify-start gap-3'}`}>
                          <span className={`flex items-center justify-center flex-shrink-0 transition-all duration-200 ease-out ${isParentActive ? 'scale-110 text-indigo-400' : 'scale-100'}`}>
                            <Icon className="w-5 h-5" />
                          </span>
                          {!isMinimized && (
                            <span className={`text-left text-sm truncate flex-1 flex items-center mt-0.5 ${isParentActive ? (isDarkMode ? 'text-white' : 'text-gray-900') : ''}`}>
                              {item.label}
                            </span>
                          )}
                        </div>
                        
                        {!isMinimized && (
                          <span className="flex-shrink-0 ml-2">
                            {isExpanded ? <FiChevronUp className="w-4 h-4 opacity-70" /> : <FiChevronDown className="w-4 h-4 opacity-70" />}
                          </span>
                        )}

                        {isMinimized && (
                          <div className={`
                            absolute left-full ml-3 px-3 py-2 rounded-lg text-sm
                            opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out
                            whitespace-nowrap z-50 pointer-events-none
                            transform translate-x-2 group-hover:translate-x-0
                            ${isDarkMode ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-100'}
                            shadow-xl border font-medium
                          `}>
                            {item.label}
                          </div>
                        )}
                      </button>

                      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded && !isMinimized ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
                        <ul className="pl-10 pr-2 py-1 space-y-1">
                          {item.subItems.map(sub => {
                            const SubIcon = sub.icon;
                            const isSubActive = activeTab === sub.id;
                            
                            return (
                              <li key={sub.id}>
                                <button
                                  onClick={() => { setActiveTab(sub.id); if (isMobile) setSidebarOpen(false); }}
                                  className={`
                                    flex items-center w-full gap-3 h-10 px-3 rounded-md
                                    transition-all duration-200 ease-out text-sm
                                    ${isSubActive
                                      ? (isDarkMode ? 'bg-white text-gray-800 font-medium shadow-sm' : 'bg-gray-800 text-white font-medium shadow-sm')
                                      : (isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-800/50' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50')
                                    }
                                  `}
                                >
                                  <SubIcon className={`w-4 h-4 ${isSubActive ? '' : 'opacity-70'}`} />
                                  <span className="truncate mt-0.5">{sub.label}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </li>
                  );
                }

                const isActive = activeTab === item.id;
                
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => { setActiveTab(item.id); if (isMobile) setSidebarOpen(false); }}
                      className={`
                        flex items-center w-full
                        group relative transition-all duration-200 ease-out
                        ${isMinimized ? 'w-11 h-11 mx-auto justify-center rounded-md' : 'h-11 px-3 justify-start rounded-md'}
                        ${isActive
                          ? (isDarkMode ? 'bg-white text-gray-800 font-medium shadow-sm shadow-white/10' : 'bg-gray-800 text-white font-medium shadow-sm shadow-gray-800/20')
                          : (isDarkMode ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900')
                        }
                      `}
                      title={isMinimized ? item.label : ''}
                    >
                      <div className={`
                        flex items-center w-full
                        ${isMinimized ? 'justify-center' : 'justify-start gap-3'}
                      `}>
                        <span className={`
                          flex items-center justify-center flex-shrink-0
                          transition-all duration-200 ease-out
                          ${isActive ? (isDarkMode ? 'scale-110 text-gray-800' : 'scale-110 text-white') : 'scale-100'}
                        `}>
                          <Icon className="w-5 h-5" />
                        </span>

                        {!isMinimized && (
                          <span className={`
                            text-left text-sm truncate flex-1 flex items-center mt-0.5
                            ${isActive ? (isDarkMode ? 'font-medium text-gray-800 tracking-wide' : 'font-medium text-white tracking-wide') : 'font-light'}
                          `}>
                            {item.label}
                          </span>
                        )}
                      </div>

                      {/* Tooltip for minimized */}
                      {isMinimized && (
                        <div className={`
                          absolute left-full ml-3 px-3 py-2 rounded-lg text-sm
                          opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out
                          whitespace-nowrap z-50 pointer-events-none
                          transform translate-x-2 group-hover:translate-x-0
                          ${isDarkMode ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-100'}
                          shadow-xl border font-medium
                        `}>
                          {item.label}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* LOGOUT */}
          <div className={`border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-50/50'} ${isMinimized ? 'px-2 py-4' : 'p-4'}`}>
            <button
              onClick={handleLogout}
              className={`
                flex items-center w-full
                group relative transition-all duration-200 ease-out
                ${isMinimized ? 'w-11 h-11 mx-auto justify-center rounded-md' : 'h-11 px-3 justify-start gap-3 rounded-md'}
                ${isDarkMode ? 'text-gray-400 hover:bg-red-500/10 hover:text-red-400' : 'text-gray-500 hover:bg-red-50 hover:text-red-600'}
              `}
              title={isMinimized ? "Sair da conta" : ""}
            >
              <span className={`
                flex items-center justify-center flex-shrink-0
                transition-all duration-200 ease-out group-hover:scale-110
              `}>
                <FiLogOut className="w-5 h-5" />
              </span>

              {!isMinimized && (
                <span className="text-left text-sm font-medium truncate flex-1 flex items-center mt-0.5">
                  Sair
                </span>
              )}

              {/* Tooltip for minimized */}
              {isMinimized && (
                <div className={`
                  absolute left-full ml-3 px-3 py-2 rounded-lg text-sm
                  opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out
                  whitespace-nowrap z-50 pointer-events-none
                  transform translate-x-2 group-hover:translate-x-0
                  ${isDarkMode ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-100'}
                  shadow-xl border font-medium
                `}>
                  Sair da conta
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Botões de Controle - No mobile só mostrar quando sidebar estiver aberto */}
      {(sidebarOpen || !isMobile) && (
        <div
          className={`
            fixed bottom-4 z-40
            will-change-transform
            ${sidebarOpen ? 'left-2' : '-left-full md:left-2'}
            transition-all duration-250 ease-out
          `}
          style={{ transform: 'translateZ(0)' }}
        >
          <div className="space-y-2">
            {/* Botão de fechar (mobile) */}
            {isMobile && sidebarOpen && (
              <div className="flex justify-center">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    p-3 rounded-xl transition-all duration-250 ease-out 
                    flex items-center gap-2 shadow-sm h-12 select-none
                    ${isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'}
                    border
                  `}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {!isMinimized && (
                    <span className="font-medium truncate">Fechar Menu</span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminSidebar;